import { Drawer, Icon, IconButton, Tabs } from '@nula/core-ui';
import styles from './canvas-shortcuts-panel.module.css';

interface ShortcutRowProps {
  label: string;
  keys: string[][];
}

function ShortcutRow({ label, keys }: ShortcutRowProps) {
  return (
    <div className={styles.row}>
      <span className={styles.label}>{label}</span>
      <span className={styles.keys}>
        {keys.map((combo, i) => (
          <span key={i} className={styles.keys}>
            {i > 0 && <span className={styles.separator}>/</span>}
            {combo.map((k, j) => (
              <kbd key={j} className={styles.key}>{k}</kbd>
            ))}
          </span>
        ))}
      </span>
    </div>
  );
}

const isMac = typeof navigator !== 'undefined' && /mac/i.test(navigator.platform);
const mod = isMac ? '⌘' : 'Ctrl';
const shift = '⇧';

const TABS = [
  {
    key: 'tools',
    label: 'Tools',
    shortcuts: [
      { label: 'Select',        keys: [['V'], ['Esc']] },
      { label: 'Hand / Pan',    keys: [['H']] },
      { label: 'Shape',         keys: [['R']] },
      { label: 'Sticky note',   keys: [['S']] },
      { label: 'Text',          keys: [['T']] },
      { label: 'Emoji',         keys: [['E']] },
      { label: 'Frame',         keys: [['F']] },
      { label: 'Draw',          keys: [['D']] },
      { label: 'Space to pan',  keys: [['Space']] },
    ],
  },
  {
    key: 'edit',
    label: 'Edit',
    shortcuts: [
      { label: 'Undo',          keys: [[mod, 'Z']] },
      { label: 'Redo',          keys: [[mod, shift, 'Z']] },
      { label: 'Copy',          keys: [[mod, 'C']] },
      { label: 'Cut',           keys: [[mod, 'X']] },
      { label: 'Paste',         keys: [[mod, 'V']] },
      { label: 'Duplicate',     keys: [[mod, 'D']] },
      { label: 'Delete',        keys: [['Del'], ['⌫']] },
      { label: 'Lock / Unlock', keys: [[mod, shift, 'L']] },
      { label: 'Group',         keys: [[mod, 'G']] },
      { label: 'Ungroup',       keys: [[mod, shift, 'G']] },
    ],
  },
  {
    key: 'selection',
    label: 'Selection',
    shortcuts: [
      { label: 'Select all',    keys: [[mod, 'A']] },
      { label: 'Deselect all',  keys: [[mod, shift, 'A'], ['Esc']] },
      { label: 'Nudge 1px',     keys: [['↑'], ['↓'], ['←'], ['→']] },
      { label: 'Nudge 10px',    keys: [[shift, '↑'], [shift, '↓'], [shift, '←'], [shift, '→']] },
      { label: 'Bring forward', keys: [[mod, ']']] },
      { label: 'Send backward', keys: [[mod, '[']] },
      { label: 'Bring to front',keys: [[mod, shift, ']']] },
      { label: 'Send to back',  keys: [[mod, shift, '[']] },
    ],
  },
  {
    key: 'view',
    label: 'View',
    shortcuts: [
      { label: 'Zoom to 100%',  keys: [[mod, '1']] },
      { label: 'Fit view',      keys: [[mod, '0']] },
      { label: 'Zoom in',       keys: [[mod, '+']] },
      { label: 'Zoom out',      keys: [[mod, '−']] },
    ],
  },
];

interface CanvasShortcutsPanelProps {
  open: boolean;
  onClose: () => void;
}

export function CanvasShortcutsPanel({ open, onClose }: CanvasShortcutsPanelProps) {
  return (
    <Drawer
      open={open}
      onClose={onClose}
      placement="bottom"
      height={300}
      mask={false}
      blocking={false}
    >
      <div className={styles.tabBar}>
        <Tabs
          size="sm"
          centered
          items={TABS.map((tab) => ({
            key: tab.key,
            label: tab.label,
            children: (
              <div className={styles.grid}>
                {tab.shortcuts.map((s) => (
                  <ShortcutRow key={s.label} label={s.label} keys={s.keys} />
                ))}
              </div>
            ),
          }))}
        />
        <IconButton
          size="sm"
          pill
          className={styles.closeBtn}
          hoverBackground="var(--surface-container-high)"
          onClick={onClose}
          aria-label="Close"
        >
          <Icon name="close" size={16} />
        </IconButton>
      </div>
    </Drawer>
  );
}
